import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MessageSquareText, Sparkles, Link2, Search, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Landing() {
  const { user, loading } = useAuth();
  const isNative = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();

  // While auth is hydrating, show a splash so we don't briefly flash the
  // marketing page to an authenticated user (especially painful on Android
  // where every cold-start starts here).
  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#3B82F6] animate-spin" />
      </div>
    );
  }

  // Signed-in users skip the marketing pitch. On native Android this is
  // critical — the WebView cold-starts at `/`, so without this redirect the
  // user always sees the Landing page and thinks they got logged out.
  if (user) return <Navigate to="/app" replace />;

  // Native app users have no need for the marketing landing page — they
  // already installed the app. Show the login screen directly instead.
  if (isNative) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen w-full bg-[#0A0A0A] text-[#F3F4F6]">
      {/* Nav */}
      <nav className="px-6 md:px-12 py-5 flex items-center justify-between border-b border-[#1E1E1E]">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-xl bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center overflow-hidden">
            <img src="/icons/logo-nl.png" alt="" className="h-6 w-6 object-contain" />
          </div>
          <span className="font-heading text-lg font-bold tracking-tight">NoteLosh</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="nav-signin-link">
            <Button variant="ghost" className="rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E]">
              Sign in
            </Button>
          </Link>
          <Link to="/login" data-testid="nav-getstarted-btn">
            <Button className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
              Get started <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 md:px-12 pt-20 pb-24 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 space-y-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E1E1E] border border-[#27272A] text-xs text-[#9CA3AF]">
              <Sparkles className="h-3 w-3 text-[#3B82F6]" /> Powered by GPT-5.2 semantic search
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
              Your links and notes,<br/>
              <span className="text-[#3B82F6]">like a chat thread.</span>
            </h1>
            <p className="text-lg text-[#9CA3AF] leading-relaxed max-w-xl">
              Create titles for every topic. Drop URLs or quick notes — they appear as messages with rich previews.
              Ask AI later in plain English. Find anything, instantly.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/login" data-testid="hero-cta-btn">
                <Button className="rounded-full h-12 px-6 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-base glow-blue">
                  Start saving — it&apos;s free <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <ChatPreview />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 md:px-12 pb-24 max-w-6xl mx-auto">
        <div className="grid sm:grid-cols-3 gap-4">
          <Feat icon={<Link2 />} title="Auto link previews"
                text="Paste any URL. We fetch the title, image and description automatically."/>
          <Feat icon={<MessageSquareText />} title="Messenger-style threads"
                text="Each title is its own thread. Browse by topic, not by folders."/>
          <Feat icon={<Search />} title="AI that gets you"
                text="Ask 'that vegan recipe I saved' — GPT-5.2 finds it by meaning."/>
        </div>
      </section>

      <footer className="border-t border-[#1E1E1E] py-8 text-center text-xs text-[#6B7280] space-y-2">
        <div>Built with care. Press <kbd className="px-1.5 py-0.5 rounded bg-[#1E1E1E] border border-[#27272A] font-mono text-[10px]">⌘K</kbd> once you&apos;re in.</div>
        <div className="flex items-center justify-center gap-3">
          <Link to="/terms" data-testid="landing-terms-link" className="hover:text-[#9CA3AF] transition-colors">Terms of Service</Link>
          <span className="text-[#27272A]">·</span>
          <Link to="/privacy" data-testid="landing-privacy-link" className="hover:text-[#9CA3AF] transition-colors">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}

function Feat({ icon, title, text }) {
  return (
    <div className="p-6 rounded-2xl bg-[#121212] border border-[#1E1E1E] hover:border-[#27272A] transition-all">
      <div className="h-10 w-10 rounded-xl bg-[#1E1E1E] flex items-center justify-center text-[#3B82F6] mb-4">
        {icon}
      </div>
      <h3 className="font-heading text-lg font-semibold mb-1.5">{title}</h3>
      <p className="text-sm text-[#9CA3AF] leading-relaxed">{text}</p>
    </div>
  );
}

function ChatPreview() {
  return (
    <div className="rounded-2xl bg-[#121212] border border-[#1E1E1E] p-4 space-y-3 shadow-[0_20px_60px_-20px_rgba(37,99,235,0.25)]">
      <div className="flex items-center gap-2 pb-3 border-b border-[#1E1E1E]">
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1E40AF] flex items-center justify-center text-xs font-semibold">R</div>
        <div>
          <div className="text-sm font-medium">React Notes</div>
          <div className="text-xs text-[#6B7280]">8 items</div>
        </div>
      </div>
      <div className="flex justify-end">
        <div className="bg-[#2563EB] text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[80%] text-sm">
          react.dev/learn — official tutorial
        </div>
      </div>
      <div className="flex justify-end">
        <div className="bg-[#1E1E1E] border border-[#27272A] rounded-xl overflow-hidden max-w-[85%]">
          <div className="h-20 bg-gradient-to-br from-[#1E40AF] to-[#0A0A0A]"/>
          <div className="p-3">
            <div className="text-xs text-[#3B82F6] mb-1">react.dev</div>
            <div className="text-sm font-medium">Quick Start – React</div>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <div className="bg-[#2563EB] text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[80%] text-sm">
          remember: useMemo vs useCallback diff
        </div>
      </div>
    </div>
  );
}
